import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatIDR, formatShortIDR, formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const Stat = ({ label, value, sub, icon: Icon, tone = "zinc", testId }) => {
  const toneMap = {
    zinc: "bg-zinc-900 text-white",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <Card data-testid={testId} className="p-5 border-zinc-200 shadow-none rounded-md">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
          <div className="mt-2 text-2xl sm:text-3xl font-display font-bold text-zinc-900 num">{value}</div>
          {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
        </div>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const canSeeReports = ["owner", "admin", "kasir"].includes(user?.role);
  const [data, setData] = useState(null);
  const [pl, setPl] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    if (canSeeReports) {
      api.get("/reports/profit-loss?months=6").then((r) => setPl(r.data));
    }
  }, [canSeeReports]);

  if (!data) return <div className="text-sm text-zinc-500">Memuat…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Ringkasan usaha kamu hari ini.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat testId="stat-inventory-value" label="Nilai Persediaan / Modal" value={formatIDR(data.inventory_value)} sub={`${formatNumber(data.product_count)} item barang`} icon={Package} tone="zinc" />
        <Stat testId="stat-sales-today" label="Penjualan Hari Ini" value={formatIDR(data.sales_today)} sub={`Bulan ini: ${formatShortIDR(data.sales_month)}`} icon={ArrowUpRight} tone="green" />
        <Stat testId="stat-profit-month" label="Laba Bulan Ini" value={formatIDR(data.laba_month)} sub={`HPP: ${formatShortIDR(data.hpp_month)}`} icon={TrendingUp} tone={data.laba_month >= 0 ? "green" : "red"} />
      </div>

      {canSeeReports && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 p-5 border-zinc-200 shadow-none rounded-md">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base sm:text-lg font-display font-semibold text-zinc-900">Penjualan & Laba 6 Bulan</h2>
                <p className="text-xs text-zinc-500">Perbandingan pendapatan dan laba per bulan</p>
              </div>
            </div>
            <div className="h-72 min-h-[280px]" data-testid="chart-profit-loss">
              {pl && (
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <BarChart data={pl.months} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => formatShortIDR(v)} />
                    <Tooltip
                      formatter={(v) => formatIDR(v)}
                      contentStyle={{ borderRadius: 6, borderColor: "#e4e4e7", fontSize: 12 }}
                    />
                    <Bar dataKey="revenue" name="Penjualan" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="profit" name="Laba" fill="#18181b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

        </div>
      )}
    </div>
  );
}
